/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useCallback, useMemo } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { useStrategySchema } from '../../../context/StrategySchemaContext';
import { nanoid } from 'nanoid/non-secure';

// Internal helper to build blank row based on columns
const buildEmptyRow = (cols) => {
  const base = { _id: nanoid(), tag: '' };
  cols.forEach(c => {
    if (['price','rr','pips','close_pct','close_initial_pct','close_lots'].includes(c.field)) base[c.field] = undefined;
    if (c.field === 'close_all') base.close_all = false;
    if (c.field === 'move_sl') base.move_sl = undefined; // can be string or object later
  });
  return base;
};

const mutuallyExclusiveGroups = (cols) => {
  const groups = {};
  cols.forEach(c => {
    if (c.mutuallyExclusiveGroup) {
      groups[c.mutuallyExclusiveGroup] = groups[c.mutuallyExclusiveGroup] || [];
      groups[c.mutuallyExclusiveGroup].push(c.field);
    }
  });
  return groups;
};

const validateRow = (row, cols, groups) => {
  const errors = [];
  // Group A (priceMode)
  if (groups.priceMode) {
    const chosen = groups.priceMode.filter(f => row[f] !== undefined && row[f] !== null && row[f] !== '');
    if (chosen.length !== 1) errors.push('Select exactly one of price/rr/pips');
    // Disallow zero values
    chosen.forEach(f => { if (Number(row[f]) === 0) errors.push('Zero not allowed for ' + f); });
  }
  // Allocation group (allocation + close_initial_pct variant)
  const allocationFields = ['close_pct','close_initial_pct','close_lots','close_all'].filter(f => f in row);
  const chosenAlloc = allocationFields.filter(f => {
    if (f === 'close_all') return !!row.close_all;
    return row[f] !== undefined && row[f] !== null && row[f] !== '';
  });
  if (chosenAlloc.length !== 1) errors.push('Select exactly one allocation (close %, initial %, lots, or All)');
  if (row.close_pct !== undefined && row.close_initial_pct !== undefined) errors.push('Cannot use close_pct and close_initial_pct together');
  return errors;
};

const formatNumber = (v) => (v === undefined || v === null || v === '' ? '' : Number(v));

const TakeProfitLadder = ({ value = [], onChange, slConfigured }) => {
  const { riskSchema, capabilities } = useStrategySchema();
  const tpDef = riskSchema?.properties?.tp_targets;
  const ui = tpDef?.['x-ui'] || {};
  const columns = ui.columns || [];
  const groups = useMemo(() => mutuallyExclusiveGroups(columns), [columns]);
  const maxTargets = capabilities?.multi_tp?.maxTargets ?? 3;

  const rows = value.map(r => ({ ...r }));

  // Derived warnings / totals
  const totals = rows.reduce((acc, r) => {
    if (r.close_pct) acc.close_pct += Number(r.close_pct || 0);
    if (r.close_initial_pct) acc.close_initial_pct += Number(r.close_initial_pct || 0);
    return acc;
  }, { close_pct: 0, close_initial_pct: 0 });

  const addRow = () => {
    if (rows.length >= maxTargets) return; // silently block (could toast)
    const next = [...rows, buildEmptyRow(columns)];
    onChange(next);
  };

  const updateRow = (id, patch) => {
    const next = rows.map(r => r._id === id ? { ...r, ...patch } : r);
    onChange(next);
  };

  const removeRow = (id) => {
    const next = rows.filter(r => r._id !== id);
    onChange(next);
  };

  const moveRow = (id, dir) => {
    const idx = rows.findIndex(r => r._id === id);
    if (idx < 0) return;
    const target = idx + dir;
    if (target < 0 || target >= rows.length) return;
    const copy = [...rows];
    const [item] = copy.splice(idx, 1);
    copy.splice(target, 0, item);
    onChange(copy);
  };

  const evaluatedRows = rows.map(r => ({
    ...r,
    _errors: validateRow(r, columns, groups)
  }));

  const hasExplicitCloseAll = evaluatedRows.some(r => r.close_all);

  const remainderHints = [];
  if (!hasExplicitCloseAll) {
    if (totals.close_pct > 0 && totals.close_pct < 0.999) remainderHints.push(`Remaining (close_pct mode): ${(1 - totals.close_pct).toFixed(2)}`);
    if (totals.close_initial_pct > 0 && totals.close_initial_pct < 0.999) remainderHints.push(`Remaining initial: ${(1 - totals.close_initial_pct).toFixed(2)}`);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">{ui.label || 'Take Profit Ladder'}</h4>
        <Button type="button" size="sm" variant="secondary" onClick={addRow} disabled={rows.length >= maxTargets}>Add Target</Button>
      </div>
      {rows.length === 0 && <p className="text-xs text-textSecondary">No targets. Add the first target.</p>}
      <div className="space-y-2">
        {evaluatedRows.map((row, idx) => (
          <div key={row._id} className={`border rounded-md p-2 bg-card/60 ${row._errors.length ? 'border-destructive/50' : 'border-borderDefault'}`}> 
            <div className="flex flex-wrap gap-3 items-end">
              {columns.map(col => (
                <LadderCell key={col.field} col={col} row={row} updateRow={updateRow} />
              ))}
              <div className="flex items-center gap-1 ml-auto">
                <Button type="button" size="icon" variant="ghost" onClick={() => moveRow(row._id, -1)} disabled={idx===0}>↑</Button>
                <Button type="button" size="icon" variant="ghost" onClick={() => moveRow(row._id, 1)} disabled={idx===rows.length-1}>↓</Button>
                <Button type="button" size="icon" variant="destructive" onClick={() => removeRow(row._id)}>✕</Button>
              </div>
            </div>
            {row._errors.length > 0 && (
              <ul className="mt-2 text-[10px] text-destructive list-disc ml-4">
                {row._errors.map((e,i) => <li key={i}>{e}</li>)}
              </ul>
            )}
          </div>
        ))}
      </div>
      <div className="text-[11px] text-textSecondary space-y-1">
        {!!remainderHints.length && remainderHints.map((h,i)=>(<div key={i}>{h} (auto close_all will apply)</div>))}
        {!slConfigured && evaluatedRows.some(r => r.rr !== undefined) && (
          <div className="text-amber-600">RR targets will be ignored until SL is defined.</div>
        )}
        {hasExplicitCloseAll && evaluatedRows.findIndex(r => r.close_all) !== evaluatedRows.length-1 && (
          <div className="text-amber-600">Rows after a close_all will be ignored.</div>
        )}
      </div>
    </div>
  );
};

const LadderCell = ({ col, row, updateRow }) => {
  const common = 'border border-borderDefault rounded px-2 h-8 text-xs bg-backgroundSurface';
  const field = col.field;

  const setValue = (val) => {
    // Mutual exclusivity: if this belongs to a group, clear siblings manually
    let patch = { [field]: val };
    if (col.mutuallyExclusiveGroup === 'priceMode') {
      ['price','rr','pips'].forEach(k => { if (k !== field) patch[k] = undefined; });
    }
    if (['close_pct','close_initial_pct','close_lots','close_all'].includes(field)) {
      // clear others
      ['close_pct','close_initial_pct','close_lots','close_all'].forEach(k => { if (k !== field) patch[k] = k==='close_all'?false:undefined; });
      if (field === 'close_all') patch.close_all = !row.close_all; // toggle
    }
    updateRow(row._id, patch);
  };

  if (field === 'move_sl') {
    const current = row.move_sl;
    const type = typeof current === 'string' ? current : current?.type || '';
    return (
      <div className="flex flex-col gap-1 w-36">
        <select className={common} value={type} onChange={(e)=>{
          const t = e.target.value;
            if (t === '') return setValue(undefined);
            if (t === 'breakeven') return setValue('breakeven');
            if (t === 'offset_rr') return setValue({ type:'offset_rr', value:1 });
            if (t === 'price') return setValue({ type:'price', value:0 });
        }}>
          <option value="">Move SL</option>
          <option value="breakeven">Breakeven</option>
          <option value="offset_rr">Offset RR</option>
          <option value="price">Price</option>
        </select>
        {type === 'offset_rr' && typeof current === 'object' && current.type==='offset_rr' && (
          <input type="number" className={common} value={current.value ?? 1} min={0.01} step={0.01} onChange={(e)=>setValue({ type:'offset_rr', value:parseFloat(e.target.value||'0') })} />
        )}
        {type === 'price' && typeof current === 'object' && current.type==='price' && (
          <input type="number" className={common} value={current.value ?? 0} step={0.0001} onChange={(e)=>setValue({ type:'price', value:parseFloat(e.target.value||'0') })} />
        )}
      </div>
    );
  }

  if (field === 'tag') {
    return <input className={common+" w-28"} placeholder="Tag" value={row.tag||''} onChange={(e)=>setValue(e.target.value)} />;
  }

  if (field === 'close_all') {
    return (
      <button type="button" className={`px-3 h-8 text-xs rounded border ${row.close_all ? 'bg-green-600 text-white border-green-600':'bg-muted/40 text-textSecondary border-borderDefault'}`} onClick={()=>setValue(!row.close_all)}>
        All
      </button>
    );
  }

  if (['price','rr','pips','close_pct','close_initial_pct','close_lots'].includes(field)) {
    return (
      <input
        type="number"
        className={common+` w-${['price'].includes(field)?'28':'20'}`}
        placeholder={col.label||field}
        value={formatNumber(row[field])}
        step={field==='pips'?1: field==='rr'?0.01: 0.0001}
        min={0}
        onChange={(e)=>{
          const v = e.target.value;
          setValue(v === '' ? undefined : parseFloat(v));
        }}
      />
    );
  }

  return null;
};

export default TakeProfitLadder;
